/**
 * Плагин ICU MessageFormat для i18next.
 *
 * Назначение файла: разбирать сообщения каталога через intl-messageformat, чтобы
 * работали plural-формы (`{count, plural, one {…} few {…} many {…}}`), выбор по значению
 * и подстановка параметров. Без этого русские и арабские формы числа выразить нельзя
 * (docs/19-localization.md §19.7, docs/adr/0005-i18n-foundation.md).
 *
 * Почему свой плагин, а не готовый i18next-icu: последний импортирует
 * intl-messageformat через default-экспорт, которого в версии 10 больше нет, и падает
 * с «IntlMessageFormat is not a constructor». Свой плагин — пятьдесят строк, одна
 * зависимость меньше и понятное поведение при ошибке разбора.
 *
 * Импорты: IntlMessageFormat (named-экспорт), типы i18next.
 *
 * Контракт плагина проверен по исходнику i18next 25: модуль с `type = "i18nFormat"`
 * получает вызов `parse(res, options, lng, ns, key, info)` вместо интерполяции.
 */

import { IntlMessageFormat } from "intl-messageformat";
import type { i18n as I18nInstance, I18nFormatModule } from "i18next";

export interface IcuFormatOptions {
  /** Кэшировать разобранные сообщения. Карточки сот перерисовываются часто. */
  readonly memoize?: boolean;
  /**
   * Что делать при ошибке разбора. По умолчанию — вернуть ключ и написать в консоль:
   * сломанное сообщение не должно ронять экран, а поломку ловит тест каталога.
   */
  readonly onError?: (error: Error, key: string, message: string) => string;
}

/** Плагин формата для i18next. Подключается как `.use(new IcuFormat())`. */
export class IcuFormat implements I18nFormatModule {
  static readonly type = "i18nFormat" as const;
  readonly type = "i18nFormat" as const;

  private readonly memoize: boolean;
  private readonly onError: NonNullable<IcuFormatOptions["onError"]>;
  private readonly cache = new Map<string, IntlMessageFormat>();

  constructor(options: IcuFormatOptions = {}) {
    this.memoize = options.memoize ?? true;
    this.onError =
      options.onError ??
      ((error, key) => {
        console.error(`Ошибка ICU-сообщения ${key}: ${error.message}`);
        return key;
      });
  }

  /** i18next вызывает init при подключении плагина. Своего состояния плагину не нужно. */
  init(_i18next: I18nInstance): void {
    this.cache.clear();
  }

  /**
   * Разбор и подстановка. Возвращает строку для интерфейса.
   *
   * @param message сырое сообщение из каталога
   * @param options параметры перевода: и подстановки, и служебные поля i18next
   * @param locale локаль, по которой выбираются формы числа
   * @param _namespace namespace ключа (не нужен, но приходит по контракту)
   * @param key полный ключ — попадает в сообщение об ошибке и в кэш
   */
  parse(
    message: string,
    options: Record<string, unknown>,
    locale: string,
    _namespace: string,
    key: string,
  ): string {
    if (typeof message !== "string" || message === "") return message;

    try {
      const cacheKey = `${locale}:${key}:${message}`;
      let formatter = this.memoize ? this.cache.get(cacheKey) : undefined;
      if (!formatter) {
        formatter = new IntlMessageFormat(message, locale);
        if (this.memoize) this.cache.set(cacheKey, formatter);
      }

      const formatted = formatter.format(options as Record<string, string | number | Date>);
      return Array.isArray(formatted) ? formatted.join("") : String(formatted);
    } catch (error) {
      return this.onError(error instanceof Error ? error : new Error(String(error)), key, message);
    }
  }

  /** Сброс кэша разобранных сообщений: нужен горячей перезагрузке текстов и тестам. */
  clearCache(): void {
    this.cache.clear();
  }
}
